import { hasArrayElements } from "@/apiUtils/commonUtils";
import { lensConfigurationSteps } from "@/apiUtils/constants";
import { endPageTransition, startPageTransition } from "@/apiUtils/pageLoadingUtils";
import {
  AdditionalOptionsData,
  AdditionalTransitionOptions,
  Price,
  TranslationData,
} from "@/common/lib/types";
import Button from "@/components/ui/button/Button";
import { translate } from "@/utils/lensConfigurator";
import React, { useMemo } from "react";
import { lensTypes } from "../../resource/config";
import CardActions from "./CardActions";
import CardImage from "./CardImage";

export type ProtectionFeature = {
  price?: Price;
  title: string;
  imageUrl: string;
  included: boolean;
  discount?: number;
  features: string[];
  description: string;
  discountedPrice?: string;
};

interface IProtectionFeatureCardProps {
  lang: string;
  lensType: string;
  currencyCode: string;
  item: AdditionalOptionsData;
  translations: TranslationData;
  transitionChoice: AdditionalTransitionOptions | undefined;
  handleTransitionChoice: (item: AdditionalOptionsData) => Promise<void> | void;
  handleStepData: (step: lensConfigurationSteps, data: any) => Promise<void> | void;
  handleTransitionSelect: (item: AdditionalTransitionOptions) => Promise<void> | void;
}

export const RecommendedBanner = ({
  isMobile,
  children,
}: {
  isMobile?: boolean;
  children: React.ReactNode;
}) => {
  const mobileClasses =
    "block w-full bg-primary-4 py-2 text-center text-[0.75rem] font-semibold md:hidden";
  const desktopClasses =
    "absolute left-4 top-4 hidden bg-primary-4 px-2 py-1 text-[0.75rem] font-semibold md:block";

  return <span className={isMobile ? mobileClasses : desktopClasses}>{children}</span>;
};

const FeatureList = ({ features }: { features: string[] }) => (
  <ul className="my-[0.625rem] grid list-inside list-disc grid-cols-2">
    {features.map((feature, index) => (
      <li key={index} className="col-span-full text-[0.813rem] sm:col-span-1">
        {feature}
      </li>
    ))}
  </ul>
);

const ProtectionFeatureCard = ({
  currencyCode,
  handleStepData,
  handleTransitionChoice,
  handleTransitionSelect,
  item,
  lang,
  lensType,
  transitionChoice,
  translations,
}: IProtectionFeatureCardProps) => {
  const currentStep = lensConfigurationSteps.ADDITIONAL_OPTIONS;
  // const [collapseSection, setCollapseSection] = useState(false);
  // const [optionInfoModal, setOptionInfoModal] = useState(false);

  const handleToggleInfo = () => {
    // setOptionInfoModal((prev) => !prev);
  };

  const handleSelect = async () => {
    startPageTransition(false);
    await handleStepData(currentStep, item);
    endPageTransition();
  };

  const pricePrefix = useMemo(() => {
    return lensType === lensTypes.COMPUTER ? "" : "+";
  }, [lensType]);

  return (
    <div>
      {/* Mobile Recommended Banner */}
      {item.badge && <RecommendedBanner isMobile>{item.badge}</RecommendedBanner>}
      <div className="relative flex flex-row items-start bg-tertiary-3">
        {/* Left Section - Image */}
        <CardImage imageUrl={item.media} badge={item.badge} />
        {/* Right Section - Content */}
        <div className="flex flex-1 flex-col p-4">
          <div>
            <h2 className="text-sm font-semibold">{translate(item.title, lang)}</h2>
            <p className="text-[0.813rem] text-tertiary-6">{item.subtitle}</p>
            <FeatureList features={item.features.map((f) => f.title)} />
            {hasArrayElements(item.learnMoreOverlay?.items) && (
              <Button
                onClick={handleToggleInfo}
                variant="link"
                className="text-[0.813rem] font-normal text-tertiary-6"
              >
                {item?.learnMoreOverlay?.badgeTitle
                  ? item.learnMoreOverlay.badgeTitle
                  : translations?.lensConfigurator?.lens_config_learn_more_button_label}
              </Button>
            )}
          </div>
          <div className="hidden lg:block">
            {/* <Divider containerClassName="mb-3 mt-4" /> */}
            {/* <TransitionLensSelector
              enableSelection={false}
              options={mockBundleLensData.at(2)?.transitionLensOptions || []}
            /> */}
            <CardActions
              item={item}
              lang={lang}
              pricePrefix={pricePrefix}
              translations={translations}
              currencyCode={currencyCode}
              onSelectOption={handleSelect}
            />
          </div>
        </div>
      </div>
      <div className="block bg-tertiary-3 lg:hidden">
        {/* <Divider containerClassName="mb-3 -mx-4" /> */}
        {/* <TransitionLensSelector
          className="mb-4"
          enableSelection={false}
          options={mockBundleLensData.at(2)?.transitionLensOptions || []}
        /> */}
        <CardActions
          isMobile
          item={item}
          lang={lang}
          pricePrefix={pricePrefix}
          translations={translations}
          currencyCode={currencyCode}
          onSelectOption={handleSelect}
        />
      </div>
    </div>
  );
};

export default ProtectionFeatureCard;
